/**
 * smallbody_view — the small bodies of a system (natural objects, 2026-10-07; catalog SB-01 … SB-07, SB-12, SY-06 … SY-10,
 * PL-16, PL-17): what a belt is made of, a belt's one named body, trojan swarms, the outer icy belt with its dwarf planet
 * and that world's large moon, a rogue world.
 *
 * ROCK IS THE SECTOR'S ROCK. Every rock here wears the sector's own asteroid master (its mesh and textures) under ONE
 * cloned material per population — a belt, a swarm, the ring: what differs between populations is how the rock takes
 * the light, never a material per rock. A population of many is ONE instanced draw.
 *
 * WHERE THINGS STAND. A compact thing (a landmark, a swarm, a world) is a root child on its own far-body anchor. The
 * outer belt is not compact — it rings the whole system — so it is cut into arcs that stand at their TRUE places with no
 * anchor (an anchored group is scaled about the ship as one: a body 2 km away in a group whose centre is 1,500 km away
 * would be drawn 130 m away, in front of everything), and an arc is drawn only within ~600 km (past ~840 km an opaque
 * shares the far plane's depth code and the sky is painted over it).
 */
import * as THREE from 'three/webgpu';
import { planetMaterial } from '../planet_look.js';
import type { NaturalEnv, ColliderGroup, PlanetHandle } from './natural_api.js';
import type { SystemMakeup, FieldMakeup, MoonMakeup } from './natural_types.js';
import {
  TAXONOMY, rockShape, landmarkParts, topShapeRadius, swarmBodies, tnoBodies, mix01,
  dwarfPlanetSpec, dwarfMoonSpec, rogueSpec, TROJAN_LOOK, tnoTint, type TaxonomyLook,
} from './world_look.js';
import { buildTail, anyPerp, type TailHandle } from './world_layers.js';
import { buildMoons, smallFace, lumpGeometry, type MoonSet } from './moon_view.js';
import { deferDisposeTree } from '../../defer_dispose.js';
import { seatFarOpaque } from '../space_light_look.js';

type V3 = [number, number, number];
type RockFinish = Pick<TaxonomyLook, 'color' | 'roughness' | 'metalness'> & { flat?: boolean };

/** The master's own material under a population's colour and finish. Ours (never `shared`): the sector's teardown frees it; its textures stay the master's. */
export function rockMaterial(base: THREE.Material, name: string, look: RockFinish): THREE.Material {
  const m = base.clone();
  m.userData = { ...m.userData, shared: false };
  const sm = m as THREE.MeshStandardMaterial;
  if (sm.color) sm.color.set(look.color);
  if ('roughness' in sm) sm.roughness = look.roughness;
  if ('metalness' in sm) sm.metalness = look.metalness;
  if (sm.emissive) sm.emissive.set(0x000000);
  if (look.flat) sm.flatShading = true;
  m.name = `natural-rock-${name}`;
  m.needsUpdate = true;
  return m;
}

export interface RockKit {
  /** A clone of the master at radius 1 (scale it to metres), every mesh wearing `mat`. */
  body(mat: THREE.Material): THREE.Group;
  material(name: string, look: RockFinish): THREE.Material;
  /** Many rocks, one draw: `at` in the mesh's own frame (m), `sizeM` each body's radius. Null when the master has no mesh. */
  instanced(mat: THREE.Material, bodies: ReadonlyArray<{ at: V3; sizeM: number }>, key: string, tint?: (i: number) => number): THREE.InstancedMesh | null;
  materials: THREE.Material[];
}
/** Read the sector's rock master once: its size, its first mesh, its material. */
export function makeRockKit(rock: THREE.Object3D): RockKit {
  rock.updateMatrixWorld(true);
  const size = new THREE.Box3().setFromObject(rock).getSize(new THREE.Vector3());
  const radius = Math.max(1e-6, Math.max(size.x, size.y, size.z) / 2);
  let first: THREE.Mesh | null = null;
  rock.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && !first) first = m; });
  const firstMesh = first as THREE.Mesh | null;
  const base: THREE.Material = firstMesh ? (Array.isArray(firstMesh.material) ? firstMesh.material[0] : firstMesh.material) : new THREE.MeshStandardMaterial({ color: 0x6a6f78, roughness: 0.95 });
  // the first mesh seated at radius 1 in the master's own frame (a cooked mesh keeps its size on its NODE, not in its vertices)
  const seatM = new THREE.Matrix4().makeScale(1 / radius, 1 / radius, 1 / radius);
  if (firstMesh) seatM.multiply(new THREE.Matrix4().copy(rock.matrixWorld).invert()).multiply(firstMesh.matrixWorld);
  const materials: THREE.Material[] = [];
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), p = new THREE.Vector3(), s = new THREE.Vector3(), c = new THREE.Color();
  return {
    materials,
    material: (name, look) => { const m = rockMaterial(base, name, look); materials.push(m); return m; },
    body: (mat) => {
      const cl = rock.clone();
      cl.position.set(0, 0, 0); cl.rotation.set(0, 0, 0); cl.scale.setScalar(1 / radius);
      cl.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = mat; });
      const g = new THREE.Group();
      g.add(cl);
      return g;
    },
    instanced: (mat, bodies, key, tint) => {
      if (!firstMesh || !bodies.length) return null;
      const im = new THREE.InstancedMesh(firstMesh.geometry, mat, bodies.length);
      for (const [i, b] of bodies.entries()) {
        const u = (k: string): number => mix01(`${key}:${i}:${k}`);
        q.setFromEuler(e.set(u('rx') * 6.283, u('ry') * 6.283, u('rz') * 6.283));
        m4.compose(p.set(b.at[0], b.at[1], b.at[2]), q, s.set(b.sizeM, b.sizeM * (0.72 + u('sy') * 0.3), b.sizeM * (0.8 + u('sz') * 0.3))).multiply(seatM);
        im.setMatrixAt(i, m4);
        if (tint) im.setColorAt(i, c.set(tint(i)));
      }
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
      im.computeBoundingSphere();
      return im;
    },
  };
}

// ── belts: taxonomy and structure (SB-01 … SB-05) ───────────────────────────────────────────────────────────────
/**
 * The sector hands each belt rock (a clone, placed and scaled) as it builds the belt; the master arrives afterwards
 * (`finish`), in the same task — so a rock takes its SHAPE at once and its belt's ONE material when the master is known.
 */
export class BeltLooks {
  private readonly rocks = new Map<number, THREE.Object3D[]>();
  readonly mats = new Map<number, THREE.Material>();
  constructor(private readonly fields: ReadonlyArray<FieldMakeup>, private readonly key: string) {}
  dress(field: number, i: number, rock: THREE.Object3D): void {
    const f = this.fields[field];
    if (!f) return;
    const sh = rockShape(f.structure, `${this.key}:belt:${field}:${i}`);
    // the sector's collider is a sphere of the rock's scale: the longest axis keeps it, the others pull in
    const k = 1 / Math.max(sh.sx, sh.sy, sh.sz, 1);
    rock.scale.set(rock.scale.x * sh.sx * k, rock.scale.y * sh.sy * k, rock.scale.z * sh.sz * k);
    let list = this.rocks.get(field);
    if (!list) { list = []; this.rocks.set(field, list); }
    list.push(rock);
  }
  finish(kit: RockKit): void {
    for (const f of this.fields) {
      const mat = kit.material(`belt${f.index}-${f.taxonomy}`, { ...TAXONOMY[f.taxonomy], flat: f.structure === 'coherent' });
      this.mats.set(f.index, mat);
      for (const r of this.rocks.get(f.index) ?? []) r.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = mat; });
    }
    this.rocks.clear();
  }
  state(): Array<Record<string, unknown>> { return this.fields.map((f) => ({ index: f.index, taxonomy: f.taxonomy, structure: f.structure, material: this.mats.get(f.index)?.name ?? null, landmark: f.landmark?.kind ?? null })); }
}

// ── everything else ─────────────────────────────────────────────────────────────────────────────────────────────
export interface SmallBodies { frame(nowS: number): void; state(): Record<string, unknown>; dispose(): void }
/** How far an arc of the outer belt is drawn (m): past ~840 km an opaque shares the far plane's depth code. */
export const TNO_SEE_M = 600_000;
const TNO_ARCS = 12;

export function buildSmallBodies(env: NaturalEnv, makeup: SystemMakeup, key: string, kit: RockKit, belts: BeltLooks): SmallBodies {
  const root = env.root();
  const owned: THREE.Object3D[] = [];
  const release: Array<() => void> = [];
  const sun = new THREE.Vector3();
  const counts = { landmarks: 0, trojans: 0, tno: 0, tnoArcs: 0, dwarf: 0, rogue: 0 };
  const tick: Array<(nowS: number) => void> = [];
  let moons: MoonSet | null = null;
  if (!root) return { frame: () => { /* torn down */ }, state: () => ({ ...counts }), dispose: () => { /* nothing */ } };

  const anchored = (name: string, pos: V3): { group: THREE.Group; abs: THREE.Vector3 } => {
    const group = new THREE.Group();
    group.name = name;
    const abs = new THREE.Vector3(pos[0], pos[1], pos[2]);
    root.add(group);
    env.anchor(group, abs);
    release.push(() => env.unanchor(group));
    owned.push(group);
    return { group, abs };
  };
  const solid = (g: ColliderGroup): void => { release.push(env.addCollider(g)); };

  // SB-04 / SB-06 / SB-07 / SY-07 — a belt's one named body
  for (const f of makeup.fields) {
    const lm = f.landmark;
    if (!lm) continue;
    const mat = belts.mats.get(f.index) ?? kit.material(`landmark${f.index}`, TAXONOMY[f.taxonomy]);
    const parts = landmarkParts(lm.kind, lm.seed), R = lm.radiusM;
    const { group, abs } = anchored(`natural-landmark:${lm.kind}`, lm.pos);
    const spin = new THREE.Group();
    group.add(spin);
    const col: ColliderGroup = { x: abs.x, y: abs.y, z: abs.z, reach: R * 4.5 + 900, list: [] };
    const lobes: Array<{ obj: THREE.Object3D; at: V3 }> = [];
    for (const [li, lobe] of parts.lobes.entries()) {
      const u = (k: string): number => mix01(`${key}:lm:${lm.seed}:${li}:${k}`);
      let body: THREE.Object3D;
      if (parts.top) {
        // a spinning top: two cones meeting at an equatorial ridge, strewn with boulders
        const top = new THREE.Mesh(lumpGeometry(R * lobe.r, lm.seed, 0.035, 1, 44, 30, topShapeRadius), mat);
        const g = new THREE.Group();
        g.add(top);
        const d = new THREE.Vector3();
        for (let b = 0; b < 18; b++) {
          const ub = (k: string): number => mix01(`${key}:lmb:${lm.seed}:${b}:${k}`);
          const cz = ub('y') * 2 - 1, th = ub('a') * 6.283, sz = Math.sqrt(1 - cz * cz);
          d.set(Math.cos(th) * sz, cz, Math.sin(th) * sz);
          const bl = kit.body(mat), rr = R * lobe.r * (0.035 + ub('s') ** 2 * 0.07);
          bl.scale.set(rr, rr * 0.8, rr);
          bl.rotation.set(ub('rx') * 6.283, ub('ry') * 6.283, 0);
          bl.position.copy(d).multiplyScalar(R * lobe.r * topShapeRadius(cz) * 0.99);
          g.add(bl);
        }
        body = g;
      } else {
        body = kit.body(mat);
        body.scale.set(R * lobe.r, R * lobe.r * lobe.squash, R * lobe.r * (0.86 + u('z') * 0.12));
        body.rotation.set(u('rx') * 0.8, u('ry') * 6.283, u('rz') * 0.8);
      }
      body.position.set(lobe.at[0] * R, lobe.at[1] * R, lobe.at[2] * R);
      spin.add(body);
      lobes.push({ obj: body, at: lobe.at });
      col.list.push({ x: abs.x + lobe.at[0] * R, y: abs.y + lobe.at[1] * R, z: abs.z + lobe.at[2] * R, r: R * lobe.r * 0.93, halfH: 0 });
    }
    solid(col);
    let tail: TailHandle | null = null;
    if (parts.tail) {
      // SB-07: a faint dust tail AWAY from the star — activity, not proof of ice
      tail = buildTail({ profile: 'dust', color: 0xd9cdb8, color2: 0xb8ac9a, seed: lm.seed, g: 0.5, segs: 24, across: 6 });
      for (const m of tail.meshes) group.add(m);
    }
    const rate = parts.top ? 0.05 : parts.orbitS > 0 ? 0.012 : 0.02;   // rad/s — a top turns visibly (it is why it is a top)
    const pv = new THREE.Vector3();
    tick.push((nowS) => {
      const a = (nowS * rate) % (Math.PI * 2), ca = Math.cos(a), sa = Math.sin(a);
      spin.rotation.y = a;
      if (parts.orbitS > 0 && lobes[1]) {
        // the companion goes round on the wall clock (counter to the spin it rides on, so its period is its own)
        const th = ((nowS / parts.orbitS) * Math.PI * 2) % (Math.PI * 2), d = lobes[1].at[0] * R;
        lobes[1].obj.position.set(Math.cos(th - a) * d, 0, -Math.sin(th - a) * d);
      }
      for (const [li, lb] of lobes.entries()) {
        const lp = lb.obj.position, c = col.list[li];
        c.x = abs.x + lp.x * ca + lp.z * sa; c.y = abs.y + lp.y; c.z = abs.z - lp.x * sa + lp.z * ca;
      }
      if (tail) {
        env.sunAt(abs, sun);
        tail.axis.copy(sun).negate();
        tail.sun.copy(sun);
        anyPerp(tail.axis, pv);
        tail.lag.copy(pv);
        tail.set(R * 0.9, R * 46, 0.5, R * 1.1, R * 16, 0.2);
      }
    });
    counts.landmarks++;
  }

  for (const f of makeup.features) {
    if (f.kind === 'trojans') {
      // SY-08: two loose swarms on a giant's orbit — dark, reddish (D / P types), never a pile
      const mat = kit.material('trojans', TROJAN_LOOK);
      for (const [si, sw] of f.swarms.entries()) {
        const { group } = anchored(`natural-trojans:${si}`, sw.pos);
        const bodies = swarmBodies(`${key}:tj:${f.seed}:${si}`, sw.count, sw.radiusM);
        const im = kit.instanced(mat, bodies, `${key}:tji:${f.seed}:${si}`);
        if (im) { im.name = 'natural-trojan-swarm'; group.add(im); }
        solid({ x: sw.pos[0], y: sw.pos[1], z: sw.pos[2], reach: sw.radiusM * 1.15 + 1_500, list: bodies.map((b) => ({ x: sw.pos[0] + b.at[0], y: sw.pos[1] + b.at[1], z: sw.pos[2] + b.at[2], r: b.sizeM * 0.9, halfH: 0 })) });
        counts.trojans += bodies.length;
      }
    } else if (f.kind === 'tno_belt') {
      // SY-10 / SB-12: a sparse ring of icy bodies, grey to muted red ("icy" is not gleaming white)
      // THE FAR OPAQUE'S SEAT: an arc is drawn at its true place to reach + 600 km — past ~1,100 km a body shares the far
      // plane's depth code and the sky pass painted over it (it popped in as the ship closed); two codes toward the eye.
      const mat = seatFarOpaque(kit.material('tno', { color: 0xffffff, roughness: 0.82, metalness: 0.02 }));
      const all = tnoBodies(`${key}:tno:${f.seed}`, f.count, f.radiusM, f.widthM);
      const arcs: Array<{ g: THREE.Group; cx: number; cy: number; cz: number; reach: number }> = [];
      const o = env.origin();
      for (let a = 0; a < TNO_ARCS; a++) {
        const mine = all.filter((b) => Math.floor((((Math.atan2(b.at[2], b.at[0]) / (Math.PI * 2)) + 1) % 1) * TNO_ARCS) === a);
        if (!mine.length) continue;
        let cx = 0, cy = 0, cz = 0;
        for (const b of mine) { cx += b.at[0]; cy += b.at[1]; cz += b.at[2]; }
        cx /= mine.length; cy /= mine.length; cz /= mine.length;
        let reach = 0;
        for (const b of mine) reach = Math.max(reach, Math.hypot(b.at[0] - cx, b.at[1] - cy, b.at[2] - cz) + b.sizeM);
        const im = kit.instanced(mat, mine.map((b) => ({ at: [b.at[0] - cx, b.at[1] - cy, b.at[2] - cz] as V3, sizeM: b.sizeM })), `${key}:tnoi:${f.seed}:${a}`, (i) => tnoTint(mine[i].red));
        if (!im) continue;
        im.name = 'natural-tno-arc';
        const g = new THREE.Group();
        g.name = `natural-tno:${a}`;
        g.position.set(cx - o.x, cy - o.y, cz - o.z); // a root child at abs − origin: the sector shifts it on every rebase
        g.add(im);
        g.visible = false;
        root.add(g);
        owned.push(g);
        arcs.push({ g, cx, cy, cz, reach });
        solid({ x: cx, y: cy, z: cz, reach: reach + 1_500, list: mine.map((b) => ({ x: b.at[0], y: b.at[1], z: b.at[2], r: b.sizeM * 0.9, halfH: 0 })) });
        counts.tno += mine.length;
      }
      counts.tnoArcs = arcs.length;
      tick.push(() => {
        const s = env.shipAbs();
        for (const a of arcs) a.g.visible = Math.hypot(a.cx - s.x, a.cy - s.y, a.cz - s.z) < a.reach + TNO_SEE_M;
      });
      // PL-17 + SY-06: its dwarf planet and that world's large moon (two worlds face to face)
      const dw = f.dwarf;
      if (dw) {
        const spec = dwarfPlanetSpec(f.seed);
        const { group, abs } = anchored('natural-dwarf', dw.pos);
        env.sunAt(abs, sun);
        const look = planetMaterial({ rim: spec.rim, atmosphere: spec.atmosphere, ocean: false, tint: spec.tint, surface: spec.surface, sunDir: sun });
        const mesh = new THREE.Mesh(new THREE.SphereGeometry(dw.radiusM, 48, 32), look.material);
        mesh.name = 'natural-dwarf-planet';
        mesh.userData.planetLook = look;
        void smallFace(spec.face).then((t) => { if (t && env.root() === root) look.setMap(t); });
        group.add(mesh);
        release.push(env.addBody({ mesh, abs, radius: dw.radiusM, name: 'Minor', spin: 0.002 }));
        solid({ x: abs.x, y: abs.y, z: abs.z, reach: dw.radiusM * 1.2 + 900, list: [{ x: abs.x, y: abs.y, z: abs.z, r: dw.radiusM * 0.98, halfH: 0 }] });
        const u = (k: string): number => mix01(`${key}:dwm:${f.seed}:${k}`);
        const mm: MoonMakeup = { kind: 'rocky', seed: f.seed + 7, orbitM: dw.moonOrbitM, inc: 0.25 + u('i') * 0.6, node: u('n') * Math.PI * 2, periodS: Math.round(12_600 + u('p') * 5_400), phase0: u('f') * Math.PI * 2, sense: 1, radiusM: dw.moonRadiusM };
        const handle: PlanetHandle = { group, mesh, radius: dw.radiusM, abs, look, set: null, name: 'Minor' };
        moons = buildMoons(env, handle, [mm], `${key}:dwarf`, [dwarfMoonSpec(f.seed)]);
        counts.dwarf = 1;
      }
    } else if (f.kind === 'rogue') {
      // PL-16: a world with no star — a dark disc that hides the stars behind it, a breath of its own heat, aurorae with no sun to drive them
      const spec = rogueSpec(f.seed);
      const { group, abs } = anchored('natural-rogue', f.pos);
      env.sunAt(abs, sun);
      const look = planetMaterial({ rim: spec.rim, atmosphere: spec.atmosphere, ocean: false, tint: spec.tint, surface: spec.surface, sunDir: sun });
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(f.radiusM, 64, 48), look.material);
      mesh.name = 'natural-rogue-world';
      mesh.userData.planetLook = look;
      void smallFace(spec.face).then((t) => { if (t && env.root() === root) look.setMap(t); });
      group.add(mesh);
      release.push(env.addBody({ mesh, abs, radius: f.radiusM, name: 'Rogue world', spin: 0.003 }));
      solid({ x: abs.x, y: abs.y, z: abs.z, reach: f.radiusM * 1.2 + 900, list: [{ x: abs.x, y: abs.y, z: abs.z, r: f.radiusM * 0.98, halfH: 0 }] });
      counts.rogue++;
    }
  }

  return {
    frame: (nowS) => { for (const t of tick) t(nowS); moons?.frame(nowS); },
    state: () => ({ ...counts, belts: belts.state(), dwarfMoon: moons?.state() ?? null }),
    dispose: () => {
      for (const r of release) { try { r(); } catch { /* released */ } }
      moons?.dispose();
      for (const o of owned) deferDisposeTree(o);
    },
  };
}
